import { BRANCHES_TERMINALES, ETAPES_DOSSIER } from "@hakili/domain-types";
import { formatDate, t } from "@hakili/i18n";
import { GuardedButton, Modal } from "@hakili/ui";
import { AlertTriangle, ArrowRightLeft, Mail, Plus, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { roleWord } from "../shell/roles.js";

/**
 * `round CN-B`, `CN-B.1`: **« Équipe », WF-CON-25 to 29, to the drawing
 * (`hakili-console.jsx`, `TeamScreen`, `InviterCollaborateurModal`,
 * `TransfertPortefeuilleModal`).**
 *
 * ## Everything counted, nothing stored
 *
 * The quality table counts open files from the door's own portfolio lines;
 * relances, response rate and satisfaction are a dash until their modules
 * exist (`R81`). « Charge et disponibilité » prints the weighted load the
 * api derived from `dossier.v_dossier_weight`, the one formula (05 §2.4.7),
 * which is the same number the dossier list sums under the same name. The
 * busiest and the least busy are named by a sentence DERIVED here from those
 * loads, never typed , the drawing's own paragraph on Bakary and Aminata is
 * its demonstration data and is not copied.
 *
 * ## The four gestures, each by its door
 *
 * - « Marquer absent » / « Rendre disponible » , `PATCH /users/{id}/availability`,
 *   the return date required, NO dossier moving (WF-CON-26);
 * - « Réaffecter » on ONE line of an expanded portfolio , the single write
 *   path with origin "team", Hakili's proposal for THAT file beside it;
 * - « Transférer tous ses dossiers » , the modal over the veil, the target
 *   simulated file by file by the door, then one write path per file with
 *   origin "portfolio-transfer" (WF-CON-27);
 * - « Retirer de l'équipe » , refused with the rose block while files are
 *   open, deactivated and dated otherwise, the person kept in the list greyed
 *   (WF-CON-28, `00-SPINE` 6.7).
 * - « Inviter » , the modal, role and scope, the professional email, the name
 *   derived from it, by the enrolment door; the invitation link is never
 *   printed (6.4). The right is `gererComptesHakili` (06 §6.1.5 row 23) ,
 *   the administration's, and since `round CN-H`, `M1(a)` (13 September
 *   2026, AMENDED BY RECORD by the founder's word) the responsable's too, for
 *   a chargé de dossier of her team: her « Inviter un chargé de dossier » is
 *   lit, the modal offers that one role, and the door refuses any other in
 *   words. The header's « Inviter un collaborateur » stays the
 *   administration's, as the drawing draws it.
 *
 * ## In words
 *
 * « Droits par rôle » , the 29-row matrix of 06 §6.1.5 has no door that
 * lists it per role; the block carries the drawing's sentences and names the
 * round (`CN-C`). The scope line under a relation manager's role (partners and
 * prospects counted) waits on `partnerships`' portfolio doors, a dash.
 */

export interface TeamProposal {
  readonly assigneeUserId: string | null;
  readonly assigneeFullName: string | null;
  readonly rule: string;
  readonly motif: string;
  readonly excluded: readonly {
    readonly userId: string;
    readonly fullName: string;
    readonly reason: string;
  }[];
}

export interface PortfolioLineView {
  readonly id: string;
  readonly reference: string;
  readonly clientName: string;
  readonly vehicleLabel: string;
  readonly amountFinanced: number;
  readonly stage: string;
  readonly weight: number;
  readonly daysInStage: number;
  readonly lateBy: number | null;
  /** `CN-C.0`: the author of the last reassignment by a person, from the door. */
  readonly reassignedBy: string | null;
  /**
   * `U-3c`, `M0 (a)`: the proposal AND the run it was recorded as. The sentence below the name,
   * its motive and its excluded are that row's own, so what the screen shows and what the audit
   * keeps are one record; the confirmation names the id (`P3-ITL-004`'s stated test).
   */
  readonly proposal: TeamProposal & { readonly runId: string | null };
  readonly targets: readonly {
    readonly userId: string;
    readonly fullName: string;
    readonly resultingLoad: number;
  }[];
}

export interface TeamMemberView {
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly role: string;
  readonly status: string;
  readonly availability: string | null;
  readonly returnsOn: string | null;
  readonly deactivatedOn: string | null;
  readonly createdAt: string;
  readonly invitationPending: boolean;
  /** `CN-C.0`: the invitation's sender and date, from the door; `null` for an account Hakili seated itself. */
  readonly invitedBy: { readonly fullName: string; readonly on: string } | null;
  readonly load: {
    readonly weightedLoad: number;
    readonly openDossiers: readonly PortfolioLineView[];
  } | null;
}

export interface TransferLineView {
  readonly dossierId: string;
  readonly reference: string;
  readonly clientName: string;
  readonly stage: string;
  readonly weight: number;
  readonly targetUserId: string | null;
  readonly targetFullName: string | null;
  readonly rule: string;
  readonly motive: string;
}

export interface TeamDoors {
  readonly readWorkload: () => Promise<readonly TeamMemberView[]>;
  readonly setAvailability: (
    userId: string,
    availability: "actif" | "absent",
    returnsOn: string | null,
  ) => Promise<void>;
  readonly reassign: (
    dossierId: string,
    userId: string,
    reason: string,
    shownRunId: string | null,
  ) => Promise<void>;
  readonly transferPreview: (userId: string) => Promise<readonly TransferLineView[]>;
  readonly transfer: (userId: string) => Promise<void>;
  readonly deactivate: (userId: string) => Promise<void>;
  readonly invite: (input: { fullName: string; email: string; role: string }) => Promise<void>;
  /**
   * `round K-1`, `M2 (d)`: the relation seat's portfolio , its count from the
   * departure's own preview (`GET /users/{id}/deactivation`, `partnersHeld`),
   * the move by `POST /users/{id}/portfolio/transfer`, `null` the
   * administration's interim.
   */
  readonly readPartnerPortfolio: (userId: string) => Promise<number | null>;
  readonly transferPartnerPortfolio: (userId: string, toUserId: string | null) => Promise<number>;
}

export interface TeamProps {
  readonly vue: string;
  /** `gererComptesHakili`, 06 §6.1.5 row 23: the administration, and the responsable for a chargé (`CN-H`). */
  readonly mayInvite: boolean;
  readonly members: readonly TeamMemberView[];
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly transferLines: readonly TransferLineView[] | null;
  readonly onSetAvailability: (
    userId: string,
    availability: "actif" | "absent",
    returnsOn: string | null,
  ) => Promise<boolean>;
  readonly onReassign: (
    dossierId: string,
    userId: string,
    reason: string,
    shownRunId: string | null,
  ) => Promise<boolean>;
  readonly onTransferPreview: (userId: string) => Promise<void>;
  readonly onTransfer: (userId: string) => Promise<boolean>;
  readonly onDeactivate: (userId: string) => Promise<boolean>;
  readonly onInvite: (input: { fullName: string; email: string; role: string }) => Promise<boolean>;
  /** `K-1`, `M2 (d)`: the seat whose portfolio was last counted, and the count. */
  readonly partnerHold: { readonly userId: string; readonly partnersHeld: number | null } | null;
  readonly onPartnerPreview: (userId: string) => Promise<number | null>;
  readonly onPartnerTransfer: (userId: string, toUserId: string | null) => Promise<number | null>;
}

/** `06-04`: the two roles that carry a portfolio , the only rows the transfer control stands on. */
const RELATION_ROLES: ReadonlySet<string> = new Set(["relationBanques", "relationConcessionnaires"]);

const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), t(key));

/** The roles' French, keyed by the wire's own code (`09-01`: data, never an identifier). */
const ROLE_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["administration", "console.equipe.roleAdministration"],
  ["responsableDossiers", "console.equipe.roleResponsable"],
  ["chargeDossier", "console.equipe.roleCharge"],
  ["relationBanques", "console.equipe.roleRelationBanques"],
  ["relationConcessionnaires", "console.equipe.roleRelationConcessionnaires"],
  ["relationAssurances", "console.equipe.roleRelationAssurances"],
  ["produitDev", "console.equipe.roleProduitDev"],
]);
const SCOPE_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["administration", "console.equipe.perimetreAdministration"],
  ["responsableDossiers", "console.equipe.perimetreResponsable"],
  ["chargeDossier", "console.equipe.perimetreCharge"],
  ["relationBanques", "console.equipe.perimetreBanques"],
  ["relationConcessionnaires", "console.equipe.perimetreConcessionnaires"],
]);
/** The invitation modal's roles, the drawing's list against the roles identity holds. */
const INVITABLE_ROLES = [
  "chargeDossier",
  "responsableDossiers",
  "relationBanques",
  "relationConcessionnaires",
  "administration",
] as const;

const STAGE_CHIP = new Map<string, string>([
  ["intake", "bg-gray-100 text-gray-600"],
  ["collecte", "bg-amber-50 text-amber-700"],
  ["expertise", "bg-sky-50 text-sky-700"],
  ["analyse", "bg-indigo-50 text-indigo-700"],
  ["pret", "bg-violet-50 text-violet-700"],
  ["soumis", "bg-emerald-50 text-emerald-700"],
  ["offre", "bg-teal-50 text-teal-700"],
  ["assurance", "bg-cyan-50 text-cyan-700"],
  ["conditions", "bg-blue-50 text-blue-700"],
  ["decaisse", "bg-emerald-100 text-emerald-800"],
  ["refuse", "bg-rose-50 text-rose-600"],
  ["refuseBanque", "bg-rose-50 text-rose-600"],
  ["sansSuite", "bg-gray-100 text-gray-600"],
]);
const STAGES = [...ETAPES_DOSSIER, ...BRANCHES_TERMINALES];
const stageShort = (id: string): string => STAGES.find((stage) => stage.id === id)?.court ?? id;

const initials = (name: string): string =>
  name
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const points = (n: number): string =>
  `${String(n)} ${n === 1 ? t("console.equipe.pointUn") : t("console.equipe.points")}`;
const openFiles = (n: number): string =>
  `${String(n)} ${n === 1 ? t("console.equipe.dossierOuvertUn") : t("console.equipe.dossiersOuverts")}`;
const dateWord = (iso: string): string => formatDate(new Date(iso.includes("T") ? iso : `${iso}T00:00:00Z`));

/** The record's three words for the availability chip. */
function availabilityChip(member: TeamMemberView): { readonly text: string; readonly classes: string } {
  if (member.availability === "desactive" || member.status === "desactive") {
    return {
      text: fill("console.equipe.compteDesactiveLe", {
        date: member.deactivatedOn === null ? DASH : dateWord(member.deactivatedOn),
      }),
      classes: "bg-gray-100 text-gray-600",
    };
  }
  if (member.availability === "absent") {
    return {
      text: fill("console.equipe.indisponibleJusquAu", {
        date: member.returnsOn === null ? DASH : dateWord(member.returnsOn),
      }),
      classes: "bg-amber-50 text-amber-700",
    };
  }
  if (member.invitationPending || member.status === "invitationEnAttente") {
    return { text: t("console.equipe.invitationEnAttente"), classes: "bg-amber-50 text-amber-700" };
  }
  return { text: t("console.equipe.disponible"), classes: "bg-emerald-50 text-emerald-700" };
}

const isOff = (member: TeamMemberView): boolean =>
  member.availability === "desactive" || member.status === "desactive";

/** The name derived from the professional address, as the drawing derives it. */
export const nameFromEmail = (address: string): string => {
  const local = address.split("@")[0] ?? "";
  if (local === "") return "";
  return local
    .split(/[._-]+/u)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/** WF-CON-25 step 6: the derived reading, never a hard-coded sentence. */
export function busiestSentence(charges: readonly TeamMemberView[]): string | null {
  // Among those who can take the next file: an absent chargé is not "the
  // least busy", she is out of the candidate set.
  const live = charges.filter((one) => one.load !== null && !isOff(one) && one.availability === "actif");
  if (live.length < 2) return null;
  const sorted = [...live].sort((a, b) => (b.load?.weightedLoad ?? 0) - (a.load?.weightedLoad ?? 0));
  const most = sorted[0];
  const least = sorted[sorted.length - 1];
  if (most === undefined || least === undefined || most.id === least.id) return null;
  return fill("console.equipe.lectureCharge", {
    plus: most.fullName,
    plusPoints: points(most.load?.weightedLoad ?? 0),
    moins: least.fullName,
    moinsPoints: points(least.load?.weightedLoad ?? 0),
  });
}

export function Team(props: TeamProps): React.JSX.Element {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [absenceOpen, setAbsenceOpen] = useState<string | null>(null);
  const [returnsOn, setReturnsOn] = useState("");
  const [transferOpen, setTransferOpen] = useState<string | null>(null);
  const [transferDone, setTransferDone] = useState(false);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [portfolioOpen, setPortfolioOpen] = useState<string | null>(null);
  const [lineOpen, setLineOpen] = useState<string | null>(null);
  const [targetChoice, setTargetChoice] = useState<Record<string, string>>({});
  // `K-1`, `M2 (d)`: the relation seat's modal, its target (`""` the interim), its outcome.
  const [portfolioSeat, setPortfolioSeat] = useState<string | null>(null);
  const [portfolioTarget, setPortfolioTarget] = useState("");
  const [portfolioDone, setPortfolioDone] = useState<{ moved: number; target: string } | null>(null);
  const [seatBlocked, setSeatBlocked] = useState<string | null>(null);

  const visible =
    props.vue === "resp"
      ? props.members.filter((one) => one.role === "chargeDossier" || one.role === "responsableDossiers")
      : props.members;
  const charges = props.members.filter((one) => one.role === "chargeDossier");
  const active = charges.filter((one) => !isOff(one));
  const totalFiles = charges.reduce((sum, one) => sum + (one.load?.openDossiers.length ?? 0), 0);
  const maxLoad = Math.max(1, ...charges.map((one) => one.load?.weightedLoad ?? 0));
  const reading = busiestSentence(charges);
  const busiest = [...active].sort((a, b) => (b.load?.weightedLoad ?? 0) - (a.load?.weightedLoad ?? 0))[0];

  const inviteControl = (label: string, small: boolean): React.JSX.Element =>
    props.mayInvite ? (
      <button
        type="button"
        data-testid={small ? "equipe-inviter-charge" : "equipe-inviter"}
        onClick={() => {
          setInviteOpen(true);
        }}
        className={
          small
            ? "flex shrink-0 items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-medium text-indigo-600 hover:bg-indigo-50"
            : "flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
        }
      >
        <Plus className={small ? "h-3.5 w-3.5" : "h-4 w-4"} aria-hidden="true" />
        {label}
      </button>
    ) : (
      <GuardedButton
        disabled
        reason={t("console.equipe.inviterMotif")}
        variant={small ? "secondary" : "primary"}
        testId={small ? "equipe-inviter-charge" : "equipe-inviter"}
      >
        <Plus className="mr-2 inline h-4 w-4" aria-hidden="true" />
        {label}
      </GuardedButton>
    );

  return (
    <div data-testid="equipe" className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("console.equipe.titre")}</h1>
          <p className="mt-1.5 text-sm text-gray-500">{t("console.equipe.sousTitre")}</p>
        </div>
        {props.vue === "admin" ? inviteControl(t("console.equipe.inviterCollaborateur"), false) : null}
      </div>

      <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: t("console.equipe.kpiCharges"),
            value: String(active.length),
            sub: t("console.equipe.kpiChargesSous"),
            highlight: false,
          },
          {
            label: t("console.equipe.kpiDossiers"),
            value: String(totalFiles),
            sub: t("console.equipe.kpiDossiersSous"),
            highlight: false,
          },
          {
            label: t("console.equipe.kpiReponse"),
            value: DASH,
            sub: t("console.equipe.kpiReponseSous"),
            highlight: true,
          },
          {
            label: t("console.equipe.kpiSatisfaction"),
            value: DASH,
            sub: t("console.equipe.kpiSatisfactionSous"),
            highlight: false,
          },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
            <p className="text-xs text-gray-500">{kpi.label}</p>
            <p
              className={
                "mt-1 text-2xl font-semibold tabular-nums " + (kpi.highlight ? "text-indigo-600" : "")
              }
            >
              {kpi.value}
            </p>
            <p className="mt-1 text-xs text-gray-500">{kpi.sub}</p>
          </div>
        ))}
      </div>

      <section>
        <h2 className="mb-3 text-sm font-semibold">{t("console.equipe.collaborateurs")}</h2>
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="flex items-center gap-4 border-b border-gray-100 px-5 py-2.5">
            <span className="flex-1 text-xs text-gray-500">{t("console.equipe.colPersonne")}</span>
            <span className="w-52 text-xs text-gray-500">{t("console.equipe.colRole")}</span>
            <span className="w-24 text-right text-xs text-gray-500">{t("console.equipe.colDossiers")}</span>
            <span className="w-24 text-right text-xs text-gray-500">{t("console.equipe.colRelances")}</span>
            <span className="w-24 text-right text-xs text-gray-500">{t("console.equipe.colReponse")}</span>
            <span className="w-28 text-right text-xs text-gray-500">
              {t("console.equipe.colSatisfaction")}
            </span>
            <span className="w-44 text-xs text-gray-500">{t("console.equipe.colStatut")}</span>
          </div>
          {visible.length === 0 ? (
            <p className="px-5 py-4 text-xs text-gray-500">{t("console.equipe.aucun")}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {visible.map((member) => {
                const off = isOff(member);
                const chip = availabilityChip(member);
                const scopeKey = SCOPE_WORDS.get(member.role);
                return (
                  <li
                    key={member.id}
                    data-testid={`membre-${member.id}`}
                    className="flex flex-wrap items-center gap-4 px-5 py-3.5"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div
                        className={
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold " +
                          (off ? "bg-gray-100 text-gray-600" : "bg-indigo-100 text-indigo-700")
                        }
                      >
                        {initials(member.fullName)}
                      </div>
                      <p
                        className={
                          "truncate text-sm font-medium " + (off ? "text-gray-500" : "text-gray-900")
                        }
                      >
                        {member.fullName}
                      </p>
                    </div>
                    <div className="w-52 shrink-0">
                      <p className="text-sm text-gray-700">{roleWord(member.role)}</p>
                      <p className="text-xs text-gray-500">{scopeKey === undefined ? DASH : t(scopeKey)}</p>
                      <p className="text-xs text-gray-500">
                        {fill("console.equipe.depuisLe", { date: dateWord(member.createdAt) })}
                      </p>
                      {member.invitedBy === null ? null : (
                        <p data-testid="equipe-invite-par" className="text-xs text-gray-500">
                          {fill("console.equipe.invitePar", {
                            nom: member.invitedBy.fullName,
                            date: dateWord(member.invitedBy.on),
                          })}
                        </p>
                      )}
                    </div>
                    <span className="w-24 shrink-0 text-right text-sm text-gray-700 tabular-nums">
                      {member.load === null ? DASH : String(member.load.openDossiers.length)}
                    </span>
                    <span className="w-24 shrink-0 text-right text-sm text-gray-500 tabular-nums">
                      {DASH}
                    </span>
                    <span className="w-24 shrink-0 text-right text-sm text-gray-500 tabular-nums">
                      {DASH}
                    </span>
                    <span className="w-28 shrink-0 text-right text-sm text-gray-500 tabular-nums">
                      {DASH}
                    </span>
                    <span className="flex w-44 shrink-0 flex-col items-start gap-2">
                      <span
                        data-testid="membre-statut"
                        className={
                          "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " + chip.classes
                        }
                      >
                        {chip.text}
                      </span>
                      {/* `K-1`, `M2 (d)`: the relation seats' two controls, the administration's (row 23), under the chip. */}
                      {props.vue === "admin" && RELATION_ROLES.has(member.role) && !off ? (
                        <div className="flex flex-col items-start gap-1.5">
                          <button
                            type="button"
                            data-testid="siege-transferer"
                            disabled={props.busy}
                            onClick={() => {
                              setPortfolioSeat(member.id);
                              setPortfolioTarget("");
                              setPortfolioDone(null);
                              setSeatBlocked(null);
                              void props.onPartnerPreview(member.id);
                            }}
                            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                          >
                            <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
                            {t("console.equipe.siegeTransferer")}
                          </button>
                          <button
                            type="button"
                            data-testid="siege-retirer"
                            disabled={props.busy}
                            onClick={() => {
                              // The door refuses while partners remain, in its own
                              // words; the block below prints them with the shortcut.
                              void (async () => {
                                const held = await props.onPartnerPreview(member.id);
                                if (held === 0) {
                                  setSeatBlocked(null);
                                  void props.onDeactivate(member.id);
                                  return;
                                }
                                setSeatBlocked(member.id);
                              })();
                            }}
                            className="text-xs font-medium text-rose-600 hover:text-rose-700"
                          >
                            {t("console.equipe.siegeRetirer")}
                          </button>
                        </div>
                      ) : null}
                    </span>
                    {seatBlocked === member.id && props.partnerHold?.userId === member.id ? (
                      <SeatDeparture
                        member={member}
                        partnersHeld={props.partnerHold.partnersHeld}
                        onTransfer={() => {
                          setPortfolioSeat(member.id);
                          setPortfolioTarget("");
                          setPortfolioDone(null);
                          setSeatBlocked(null);
                        }}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold">{t("console.equipe.chargeTitre")}</h2>
            <p className="mt-0.5 text-sm text-gray-500">{t("console.equipe.chargeSous")}</p>
          </div>
          {inviteControl(t("console.equipe.inviterCharge"), true)}
        </div>
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {charges.length === 0 ? (
            <p className="px-5 py-4 text-xs text-gray-500">{t("console.equipe.aucunCharge")}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {charges.map((member) => {
                const off = isOff(member);
                const absent = member.availability === "absent";
                const load = member.load?.weightedLoad ?? 0;
                const files = member.load?.openDossiers ?? [];
                const chip = availabilityChip(member);
                return (
                  <li key={member.id} data-testid={`charge-${member.id}`} className="px-5 py-3.5">
                    <div className="flex items-center gap-4">
                      <div className="w-48 shrink-0">
                        <p className={"text-sm font-medium " + (off ? "text-gray-500" : "text-gray-900")}>
                          {member.fullName}
                        </p>
                        <p data-testid="charge-points" className="text-xs text-gray-500">
                          {points(load)} · {openFiles(files.length)}
                        </p>
                        {member.invitedBy === null ? null : (
                          <p data-testid="charge-invite-par" className="text-xs text-gray-500">
                            {fill("console.equipe.invitePar", {
                              nom: member.invitedBy.fullName,
                              date: dateWord(member.invitedBy.on),
                            })}
                          </p>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className={
                              "h-2 rounded-full " + (off || absent ? "bg-gray-300" : "bg-indigo-500")
                            }
                            style={{ width: `${String(Math.trunc((load / maxLoad) * 100))}%` }}
                          />
                        </div>
                      </div>
                      <span className="w-44 shrink-0 text-right">
                        <span
                          className={
                            "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " + chip.classes
                          }
                        >
                          {chip.text}
                        </span>
                      </span>
                    </div>

                    {off ? null : (
                      <div className="mt-2.5 flex flex-wrap items-center gap-2">
                        {absent ? (
                          <button
                            type="button"
                            data-testid="charge-rendre-disponible"
                            disabled={props.busy}
                            onClick={() => {
                              void props.onSetAvailability(member.id, "actif", null);
                            }}
                            className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                          >
                            {t("console.equipe.rendreDisponible")}
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-testid="charge-marquer-absent"
                            onClick={() => {
                              setAbsenceOpen(absenceOpen === member.id ? null : member.id);
                              setBlocked(null);
                            }}
                            className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                          >
                            {t("console.equipe.marquerAbsent")}
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid="charge-voir-dossiers"
                          disabled={files.length === 0}
                          onClick={() => {
                            // WF-CON-25's acceptance: ONE portfolio at a time.
                            setPortfolioOpen(portfolioOpen === member.id ? null : member.id);
                            setLineOpen(null);
                            setBlocked(null);
                          }}
                          className={
                            "rounded-lg border px-2.5 py-1.5 text-xs font-medium disabled:opacity-40 " +
                            (portfolioOpen === member.id
                              ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                              : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50")
                          }
                        >
                          {portfolioOpen === member.id
                            ? t("console.equipe.masquerDossiers")
                            : fill("console.equipe.voirDossiers", { n: String(files.length) })}
                        </button>
                        <button
                          type="button"
                          data-testid="charge-transferer"
                          disabled={files.length === 0 || props.busy}
                          onClick={() => {
                            setTransferOpen(member.id);
                            setTransferDone(false);
                            setBlocked(null);
                            void props.onTransferPreview(member.id);
                          }}
                          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                        >
                          <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
                          {t("console.equipe.transfererTout")}
                        </button>
                        <button
                          type="button"
                          data-testid="charge-retirer"
                          disabled={props.busy}
                          onClick={() => {
                            // WF-CON-28: refused while files are open , the order
                            // is enforced here in the drawing's words and by the
                            // door in its own.
                            if (files.length > 0) {
                              setBlocked(member.id);
                              return;
                            }
                            setBlocked(null);
                            void props.onDeactivate(member.id);
                          }}
                          className="text-xs font-medium text-rose-600 hover:text-rose-700"
                        >
                          {t("console.equipe.retirer")}
                        </button>
                      </div>
                    )}

                    {portfolioOpen === member.id ? (
                      <div
                        data-testid="portefeuille"
                        className="mt-3 overflow-hidden rounded-xl border border-gray-200"
                      >
                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                          <p className="text-xs font-medium text-gray-700">
                            {fill("console.equipe.enCoursDe", { nom: member.fullName })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {openFiles(files.length)} · {points(load)}
                          </p>
                        </div>
                        <ul className="divide-y divide-gray-50">
                          {files.map((line) => {
                            const chosen =
                              targetChoice[line.id] ??
                              line.proposal.assigneeUserId ??
                              line.targets[0]?.userId ??
                              "";
                            const chosenTarget = line.targets.find((one) => one.userId === chosen);
                            return (
                              <li key={line.id} data-testid={`ligne-${line.id}`} className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-52 min-w-0 shrink-0">
                                    <p className="truncate text-sm font-medium text-gray-900">
                                      {line.clientName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {line.reference} · {line.vehicleLabel}
                                    </p>
                                    {line.reassignedBy === null ? null : (
                                      <p data-testid="ligne-reaffecte-par" className="text-xs text-gray-500">
                                        {fill("console.equipe.reaffectePar", { nom: line.reassignedBy })}
                                      </p>
                                    )}
                                  </div>
                                  <span className="w-40 shrink-0">
                                    <span
                                      className={
                                        "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
                                        (STAGE_CHIP.get(line.stage) ?? "bg-gray-100 text-gray-600")
                                      }
                                    >
                                      {stageShort(line.stage)}
                                    </span>
                                  </span>
                                  <span
                                    data-testid="ligne-poids"
                                    className="w-24 shrink-0 text-xs text-gray-500 tabular-nums"
                                  >
                                    {points(line.weight)}
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    {line.lateBy === null ? null : (
                                      <span
                                        data-testid="ligne-stagnation"
                                        className="text-xs font-medium text-rose-600"
                                      >
                                        {fill("console.equipe.stagnation", {
                                          jours: String(line.daysInStage),
                                          attendus: String(line.daysInStage - line.lateBy),
                                        })}
                                      </span>
                                    )}
                                  </span>
                                  <button
                                    type="button"
                                    data-testid="ligne-reaffecter"
                                    onClick={() => {
                                      setLineOpen(lineOpen === line.id ? null : line.id);
                                    }}
                                    className="shrink-0 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                                  >
                                    {t("console.arrivees.reaffecter")}
                                  </button>
                                </div>
                                {lineOpen === line.id ? (
                                  <div
                                    data-testid="ligne-panneau"
                                    className="mt-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
                                  >
                                    <div className="mb-2 flex items-start gap-2">
                                      <Sparkles
                                        className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600"
                                        aria-hidden="true"
                                      />
                                      <div>
                                        <p className="text-xs leading-relaxed text-gray-700">
                                          {line.proposal.assigneeFullName === null
                                            ? t("console.equipe.aucunCharge")
                                            : fill("console.equipe.hakiliProposerait", {
                                                nom: line.proposal.assigneeFullName,
                                              })}
                                        </p>
                                        <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                                          {line.proposal.motif}
                                        </p>
                                        {line.proposal.excluded.length > 0 ? (
                                          <p className="mt-0.5 text-sm leading-relaxed text-amber-700">
                                            {t("console.equipe.nonRetenus")}{" "}
                                            {line.proposal.excluded
                                              .map((one) => `${one.fullName}, ${one.reason}`)
                                              .join(" ; ")}
                                          </p>
                                        ) : (
                                          <p className="mt-0.5 text-sm leading-relaxed text-gray-500">
                                            {t("console.arrivees.nonRetenusMotif")}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <select
                                        data-testid="ligne-cible"
                                        value={chosen}
                                        onChange={(event) => {
                                          setTargetChoice((now) => ({
                                            ...now,
                                            [line.id]: event.target.value,
                                          }));
                                        }}
                                        className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-indigo-400"
                                      >
                                        {line.targets.map((one) => (
                                          <option key={one.userId} value={one.userId}>
                                            {one.fullName}
                                          </option>
                                        ))}
                                      </select>
                                      <button
                                        type="button"
                                        data-testid="ligne-confirmer"
                                        disabled={chosen === "" || props.busy}
                                        onClick={() => {
                                          void (async () => {
                                            const ok = await props.onReassign(
                                              line.id,
                                              chosen,
                                              line.proposal.motif,
                                              line.proposal.runId,
                                            );
                                            if (ok) setLineOpen(null);
                                          })();
                                        }}
                                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                                      >
                                        {t("console.equipe.confirmerReaffectation")}
                                      </button>
                                      {chosenTarget === undefined ? null : (
                                        <span
                                          data-testid="ligne-charge-cible"
                                          className="text-xs text-gray-500"
                                        >
                                          {chosenTarget.fullName} · {points(chosenTarget.resultingLoad)}{" "}
                                          {t("console.equipe.deCharge")}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                        <p className="px-4 py-2.5 text-sm leading-relaxed text-gray-500">
                          {t("console.equipe.portefeuilleNote")}
                        </p>
                      </div>
                    ) : null}

                    {absenceOpen === member.id ? (
                      <div
                        data-testid="absence-formulaire"
                        className="mt-2.5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3"
                      >
                        <p className="mb-2 text-xs leading-relaxed text-gray-600">
                          {fill("console.equipe.absencePhrase", { nom: member.fullName })}
                        </p>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">
                            {t("console.equipe.retourLe")}
                            <input
                              type="date"
                              data-testid="absence-retour"
                              value={returnsOn}
                              onChange={(event) => {
                                setReturnsOn(event.target.value);
                              }}
                              className="ml-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-indigo-400"
                            />
                          </label>
                          <button
                            type="button"
                            data-testid="absence-confirmer"
                            disabled={returnsOn === "" || props.busy}
                            onClick={() => {
                              void (async () => {
                                const ok = await props.onSetAvailability(member.id, "absent", returnsOn);
                                if (ok) {
                                  setAbsenceOpen(null);
                                  setReturnsOn("");
                                }
                              })();
                            }}
                            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                          >
                            {t("console.equipe.confirmerAbsence")}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAbsenceOpen(null);
                            }}
                            className="text-xs font-medium text-gray-500 hover:text-gray-700"
                          >
                            {t("console.equipe.annuler")}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {blocked === member.id && files.length > 0 ? (
                      <div
                        data-testid="retrait-bloque"
                        className="mt-2.5 flex items-start gap-2.5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3"
                      >
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
                        <div className="flex-1">
                          <p className="text-sm leading-relaxed text-rose-700">
                            {fill("console.equipe.sortieImpossible", { n: String(files.length) })}
                          </p>
                          <button
                            type="button"
                            data-testid="retrait-transferer"
                            onClick={() => {
                              setTransferOpen(member.id);
                              setTransferDone(false);
                              setBlocked(null);
                              void props.onTransferPreview(member.id);
                            }}
                            className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                          >
                            {t("console.equipe.transfererPortefeuille")}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          <p className="px-5 py-3 text-sm leading-relaxed text-gray-500">{t("console.equipe.chargeNote")}</p>
        </div>
      </section>

      <div className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
        <div>
          <p data-testid="equipe-lecture" className="text-sm leading-relaxed text-gray-600">
            {reading ?? t("console.equipe.lectureAucune")}
          </p>
          {reading !== null && busiest !== undefined ? (
            <button
              type="button"
              data-testid="equipe-ouvrir-plus-charge"
              onClick={() => {
                setPortfolioOpen(busiest.id);
                setLineOpen(null);
              }}
              className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              {fill("console.equipe.ouvrirPortefeuilleDe", { nom: busiest.fullName })}
            </button>
          ) : null}
          <p className="mt-2 text-sm leading-relaxed text-gray-500">{t("console.equipe.lectureNote")}</p>
        </div>
      </div>

      <section>
        <h2 className="mb-1 text-sm font-semibold">{t("console.equipe.droitsTitre")}</h2>
        <p className="mb-3 text-sm text-gray-500">{t("console.equipe.droitsSous")}</p>
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
          <p className="text-sm text-gray-500">{t("console.equipe.droitsRond")}</p>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">{t("console.equipe.droitsNote1")}</p>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">{t("console.equipe.droitsNote2")}</p>
        </div>
      </section>

      {props.refusal === null ? null : (
        <p role="alert" data-testid="equipe-refus" className="text-sm text-rose-600">
          {props.refusal}
        </p>
      )}

      <PortfolioModal
        member={
          portfolioSeat === null ? null : (props.members.find((one) => one.id === portfolioSeat) ?? null)
        }

        members={props.members}

        partnersHeld={props.partnerHold?.userId === portfolioSeat ? props.partnerHold.partnersHeld : null}

        target={portfolioTarget}

        done={portfolioDone}

        busy={props.busy}

        onTarget={setPortfolioTarget}

        onClose={() => {
          setPortfolioSeat(null);
        }}

        onConfirm={() => {
          if (portfolioSeat === null) return;

          const targetName =
            portfolioTarget === ""
              ? t("console.equipe.portefeuilleAdministration")
              : (props.members.find((one) => one.id === portfolioTarget)?.fullName ?? portfolioTarget);

          void props
            .onPartnerTransfer(portfolioSeat, portfolioTarget === "" ? null : portfolioTarget)
            .then((moved) => {
              if (moved !== null) setPortfolioDone({ moved, target: targetName });
            });
        }}
      />

      <TransferModal
        member={props.members.find((one) => one.id === transferOpen) ?? null}
        lines={props.transferLines}
        done={transferDone}
        busy={props.busy}
        onClose={() => {
          setTransferOpen(null);
          setTransferDone(false);
        }}
        onConfirm={async (userId) => {
          const ok = await props.onTransfer(userId);
          if (ok) setTransferDone(true);
        }}
      />

      <InviteModal
        open={inviteOpen}
        // `M1(a)`: the responsable's grant is scoped to a chargé de dossier of
        // her team; the administration seats every role.
        roles={props.vue === "admin" ? INVITABLE_ROLES : ["chargeDossier"]}
        busy={props.busy}
        onClose={() => {
          setInviteOpen(false);
        }}
        onInvite={async (input) => {
          const ok = await props.onInvite(input);
          if (ok) setInviteOpen(false);
        }}
      />
    </div>
  );
}

/** WF-CON-27: the modal over the veil, Hakili's target file by file, the load simulated by the door. */
function TransferModal(props: {
  readonly member: TeamMemberView | null;
  readonly lines: readonly TransferLineView[] | null;
  readonly done: boolean;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (userId: string) => Promise<void>;
}): React.JSX.Element | null {
  const member = props.member;
  if (member === null) return null;
  const lines = props.lines ?? [];
  const impossible = lines.some((line) => line.targetUserId === null);
  return (
    <Modal
      open
      title={fill("console.equipe.transfererTitre", { nom: member.fullName })}
      onClose={props.onClose}
      closeLabel={t("console.partenaires.fermer")}
      className="max-w-xl"
    >
      <div data-testid="transfert-modal">
        <p className="mb-4 text-xs leading-relaxed text-gray-500">
          {fill("console.equipe.transfererPhrase", { n: String(lines.length) })}
        </p>
        {props.done ? (
          <div
            data-testid="transfert-fait"
            className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4"
          >
            <p className="text-sm font-medium leading-relaxed text-emerald-800">
              {fill("console.equipe.transfereFait", { nom: member.fullName })}
            </p>
            <button
              type="button"
              onClick={props.onClose}
              className="mt-3 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {t("console.partenaires.fermer")}
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 overflow-hidden rounded-2xl border border-gray-100">
              <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
                <Sparkles className="h-4 w-4 text-indigo-600" aria-hidden="true" />
                <p className="text-xs font-medium text-gray-700">{t("console.equipe.propositionMotivee")}</p>
              </div>
              {props.lines === null ? (
                <p className="px-4 py-3 text-xs text-gray-500">{t("console.session.chargement")}</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {lines.map((line) => (
                    <li
                      key={line.dossierId}
                      data-testid={`transfert-${line.dossierId}`}
                      className="px-4 py-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{line.clientName}</p>
                          <p className="text-xs text-gray-500">
                            {line.reference} · {stageShort(line.stage)} · {points(line.weight)}
                          </p>
                        </div>
                        <span
                          data-testid="transfert-cible"
                          className="shrink-0 text-sm font-medium text-indigo-700"
                        >
                          {line.targetFullName ?? t("console.equipe.aucunChargeDisponible")}
                        </span>
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-gray-500">{line.motive}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={props.onClose}
                className="text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                {t("console.equipe.annuler")}
              </button>
              <button
                type="button"
                data-testid="transfert-confirmer"
                disabled={impossible || props.lines === null || lines.length === 0 || props.busy}
                onClick={() => {
                  void props.onConfirm(member.id);
                }}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {lines.length > 1
                  ? fill("console.equipe.transfererLesN", { n: String(lines.length) })
                  : t("console.equipe.transfererLeDossier")}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** WF-CON-29: role and scope, the professional email, the name derived; disabled until it yields one. */
function InviteModal(props: {
  readonly open: boolean;
  readonly roles: readonly string[];
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onInvite: (input: { fullName: string; email: string; role: string }) => Promise<void>;
}): React.JSX.Element | null {
  const [role, setRole] = useState<string>("chargeDossier");
  const [email, setEmail] = useState("");
  const name = nameFromEmail(email.trim());
  const scopeKey = SCOPE_WORDS.get(role);
  return (
    <Modal
      open={props.open}
      title={t("console.equipe.inviterCollaborateur")}
      onClose={props.onClose}
      closeLabel={t("console.partenaires.fermer")}
    >
      <div data-testid="invitation-modal">
        <p className="mb-4 text-xs text-gray-500">{t("console.equipe.equipeHakili")}</p>
        <label className="mb-1.5 block text-xs font-medium text-gray-600">
          {t("console.equipe.role")}
          <select
            data-testid="invitation-role"
            value={role}
            onChange={(event) => {
              setRole(event.target.value);
            }}
            className="mt-1.5 mb-3 block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
          >
            {props.roles.map((code) => {
              const key = ROLE_WORDS.get(code);
              return (
                <option key={code} value={code}>
                  {key === undefined ? code : t(key)}
                </option>
              );
            })}
          </select>
        </label>
        <p className="mb-1 text-xs font-medium text-gray-600">{t("console.equipe.perimetre")}</p>
        <p data-testid="invitation-perimetre" className="mb-1 text-sm text-gray-700">
          {scopeKey === undefined ? DASH : t(scopeKey)}
        </p>
        <p className="mb-3 text-sm leading-relaxed text-gray-500">{t("console.equipe.perimetreNote")}</p>
        <label className="mb-1.5 block text-xs font-medium text-gray-600">
          {t("console.equipe.adressePro")}
          <div className="mt-1.5 mb-1 flex items-center gap-2.5 rounded-xl border border-gray-200 px-3 py-2.5">
            <Mail className="h-4 w-4 text-gray-500" aria-hidden="true" />
            <input
              data-testid="invitation-email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              placeholder="prenom.nom@hakili.finance"
              className="flex-1 text-sm outline-none"
            />
          </div>
        </label>
        <p data-testid="invitation-nom" className="mb-5 text-sm leading-relaxed text-gray-500">
          {t("console.equipe.invitationNote")}
          {role === "chargeDossier" && name !== ""
            ? ` ${fill("console.equipe.entreraDansEquipe", { nom: name })}`
            : ""}
        </p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={props.onClose}
            className="text-sm font-medium text-gray-600 hover:text-gray-700"
          >
            {t("console.equipe.annuler")}
          </button>
          <button
            type="button"
            data-testid="invitation-envoyer"
            disabled={name === "" || !email.includes("@") || props.busy}
            onClick={() => {
              void props.onInvite({ fullName: name, email: email.trim(), role });
            }}
            className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {t("console.equipe.envoyerInvitation")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- the wrapper

/**
 * `K-1`, `M2 (d)`: the departure refused with the count and the shortcut ,
 * WF-CON-40's rose block, the sentence the door's own (`PORTFOLIO_BLOCKED`).
 * An empty portfolio lets the removal through as before.
 */
function SeatDeparture(props: {
  readonly member: TeamMemberView;
  readonly partnersHeld: number | null;
  readonly onTransfer: () => void;
}): React.JSX.Element {
  return (
    <div
      data-testid="siege-retrait-bloque"
      className="mt-1 flex basis-full items-start gap-2.5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
      <div className="flex-1">
        <p className="text-sm leading-relaxed text-rose-700">
          {props.partnersHeld === null
            ? t("common.valeur.tiret")
            : fill("console.equipe.siegeSortieImpossible", { n: String(props.partnersHeld) })}
        </p>
        <button
          type="button"
          data-testid="siege-retrait-transferer"
          onClick={props.onTransfer}
          className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
        >
          {t("console.equipe.siegeTransferer")}
        </button>
      </div>
    </div>
  );
}

/**
 * `K-1`, `M2 (d)`: the portfolio's modal to the drawing's style
 * (`TransfertPortefeuilleModal`), the partners counted by the door, the
 * target among the seats of the same type or « à l'administration ».
 */
function PortfolioModal(props: {
  readonly member: TeamMemberView | null;
  readonly members: readonly TeamMemberView[];
  readonly partnersHeld: number | null;
  readonly target: string;
  readonly done: { moved: number; target: string } | null;
  readonly busy: boolean;
  readonly onTarget: (userId: string) => void;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
}): React.JSX.Element | null {
  const member = props.member;
  if (member === null) return null;
  const targets = props.members.filter(
    (one) => one.id !== member.id && one.role === member.role && one.status === "actif",
  );
  const targetName =
    props.target === ""
      ? t("console.equipe.portefeuilleAdministration")
      : (targets.find((one) => one.id === props.target)?.fullName ?? props.target);
  return (
    <Modal
      open
      title={fill("console.equipe.portefeuilleTitre", { nom: member.fullName })}
      onClose={props.onClose}
      closeLabel={t("console.partenaires.fermer")}
      className="max-w-xl"
    >
      <div data-testid="portefeuille-modal">
        {props.done !== null ? (
          <div
            data-testid="portefeuille-fait"
            className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4"
          >
            <p className="text-sm font-medium leading-relaxed text-emerald-800">
              {fill("console.equipe.portefeuilleFait", {
                n: String(props.done.moved),
                cible: props.done.target,
                nom: member.fullName,
              })}
            </p>
            <button
              type="button"
              onClick={props.onClose}
              className="mt-3 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {t("console.partenaires.fermer")}
            </button>
          </div>
        ) : props.partnersHeld === null ? (
          <p className="text-sm text-gray-500">{t("console.session.chargement")}</p>
        ) : props.partnersHeld === 0 ? (
          <p data-testid="portefeuille-aucun" className="text-sm text-gray-500">
            {t("console.equipe.portefeuilleAucun")}
          </p>
        ) : (
          <>
            <p data-testid="portefeuille-compte" className="mb-4 text-xs leading-relaxed text-gray-500">
              {fill("console.equipe.portefeuillePhrase", { n: String(props.partnersHeld) })}
            </p>
            <label className="mb-1 block text-xs text-gray-500" htmlFor="portefeuille-cible">
              {t("console.equipe.portefeuilleCible")}
            </label>
            <select
              id="portefeuille-cible"
              data-testid="portefeuille-cible"
              value={props.target}
              onChange={(event) => {
                props.onTarget(event.target.value);
              }}
              className="mb-4 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">{t("console.equipe.portefeuilleAdministration")}</option>
              {targets.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.fullName}
                </option>
              ))}
            </select>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={props.onClose}
                className="text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                {t("console.equipe.annuler")}
              </button>
              <button
                type="button"
                data-testid="portefeuille-confirmer"
                disabled={props.busy}
                onClick={props.onConfirm}
                className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {fill("console.equipe.portefeuilleConfirmer", {})} · {targetName}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export interface TeamScreenProps extends TeamDoors {
  readonly vue: string;
  readonly mayInvite: boolean;
}

const failureWords = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

export function TeamScreen(props: TeamScreenProps): React.JSX.Element | null {
  const [members, setMembers] = useState<readonly TeamMemberView[] | null>(null);
  const [transferLines, setTransferLines] = useState<readonly TransferLineView[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [partnerHold, setPartnerHold] = useState<{ userId: string; partnersHeld: number | null } | null>(
    null,
  );
  const { readWorkload } = props;

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answered = await readWorkload();
        if (run.live) setMembers(answered);
      } catch (failure) {
        if (run.live) setRefusal(failureWords(failure));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readWorkload, epoch]);

  if (members === null) {
    return refusal === null ? null : (
      <p role="alert" data-testid="equipe-refus" className="text-sm text-rose-600">
        {refusal}
      </p>
    );
  }

  const act = (gesture: () => Promise<void>): Promise<boolean> =>
    (async () => {
      setBusy(true);
      setRefusal(null);
      try {
        await gesture();
        setEpoch((now) => now + 1);
        return true;
      } catch (failure) {
        setRefusal(failureWords(failure));
        return false;
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Team
      vue={props.vue}
      mayInvite={props.mayInvite}
      members={members}
      busy={busy}
      refusal={refusal}
      transferLines={transferLines}
      onSetAvailability={(userId, availability, returnsOn) =>
        act(() => props.setAvailability(userId, availability, returnsOn))
      }
      onReassign={(dossierId, userId, reason, shownRunId) =>
        act(() => props.reassign(dossierId, userId, reason, shownRunId))
      }
      onTransferPreview={async (userId) => {
        setTransferLines(null);
        try {
          setTransferLines(await props.transferPreview(userId));
        } catch (failure) {
          setRefusal(failureWords(failure));
        }
      }}
      onTransfer={(userId) => act(() => props.transfer(userId))}
      onDeactivate={(userId) => act(() => props.deactivate(userId))}
      onInvite={(input) => act(() => props.invite(input))}
      partnerHold={partnerHold}
      onPartnerPreview={async (userId) => {
        setPartnerHold({ userId, partnersHeld: null });
        try {
          const partnersHeld = await props.readPartnerPortfolio(userId);
          setPartnerHold({ userId, partnersHeld });
          return partnersHeld;
        } catch (failure) {
          setRefusal(failureWords(failure));
          return null;
        }
      }}
      onPartnerTransfer={async (userId, toUserId) => {
        setBusy(true);
        setRefusal(null);
        try {
          const moved = await props.transferPartnerPortfolio(userId, toUserId);
          setPartnerHold({ userId, partnersHeld: 0 });
          setEpoch((now) => now + 1);
          return moved;
        } catch (failure) {
          setRefusal(failureWords(failure));
          return null;
        } finally {
          setBusy(false);
        }
      }}
    />
  );
}
